import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../../../ui/button';
import { RTLInput, RTLTextarea } from '../../../ui/rtl-input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Switch } from '../../../ui/switch';
import { Label } from '../../../ui/label';
import { toast } from '../../../../lib/toast-utils';
import { useComponentImages } from '../../../../hooks/useComponentImages';
import { BackgroundImageUploader } from '../BackgroundImageUploader';
import { AIButton } from '../../../ui/ai-button';
import { useAIGeneration } from '../../../../hooks/useAIGeneration';
import { FieldType } from '../../../../lib/ai/characterLimits';
import { useAuth } from '../../../../lib/auth/AuthContext';
import { Id } from '../../../../convex/_generated/dataModel';
import { HeroContent, FileId } from '../../../../types/pageBuilder';
import { DEFAULT_HERO_CONTENT } from '../../../../constants/pageBuilderDefaults';

interface HeroSectionProps {
  projectId: string;
  selectedLanguage: string;
  heroComponent: any;
  heroContent: HeroContent;
  setHeroContent: React.Dispatch<React.SetStateAction<HeroContent>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  refreshComponents: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  projectId,
  selectedLanguage,
  heroComponent,
  heroContent,
  setHeroContent,
  isLoading,
  setIsLoading,
  refreshComponents
}) => {
  const { useAuthenticatedMutation } = useAuth();
  const { generate, isGenerating } = useAIGeneration();
  
  // Mutations
  const saveComponent = useAuthenticatedMutation('pageComponents:upsert');
  
  // Use the component images hook to get image URLs
  const { getImageUrl } = useComponentImages(
    projectId ? projectId as Id<"projects"> : undefined,
    heroComponent?._id
  );
  
  // Memoize image URLs to prevent unnecessary re-renders
  const heroBackgroundImageUrl = useMemo(() => {
    if (heroComponent?.content?.backgroundImageId) {
      const url = getImageUrl(heroComponent.content.backgroundImageId) || 
             heroComponent.content.backgroundImage || '';
      
      console.log('Resolved hero background image URL:', {
        backgroundImageId: heroComponent.content.backgroundImageId,
        resolvedUrl: url,
        originalUrl: heroComponent.content.backgroundImage
      });
      
      return url;
    }
    return heroComponent?.content?.backgroundImage || '';
  }, [heroComponent, getImageUrl]);

  // Set initial state if there's an existing background image
  useEffect(() => {
    // Skip if heroComponent is still undefined (loading)
    if (heroComponent === undefined) return;
    
    if (heroComponent) {
      console.log('Setting hero content from existing component:', heroComponent);
      
      setHeroContent(prev => {
        // Check if we need to update by comparing key fields
        const needsUpdate = 
          prev.title !== heroComponent.content.title ||
          prev.subtitle !== heroComponent.content.subtitle ||
          prev.backgroundImage !== heroBackgroundImageUrl ||
          prev.backgroundImageId !== heroComponent.content.backgroundImageId ||
          JSON.stringify(prev.highlights) !== JSON.stringify(heroComponent.content.highlights || ['', '', '', '']) ||
          prev.primaryButtonText !== (heroComponent.content.primaryButtonText || 'Download Brochure') ||
          prev.secondaryButtonText !== (heroComponent.content.secondaryButtonText || 'Speak to Property Expert') ||
          prev.showDownloadForm !== (heroComponent.content.showDownloadForm !== undefined ? heroComponent.content.showDownloadForm : true);
        
        if (!needsUpdate) {
          return prev; // No change needed
        }
        
        return {
          ...heroComponent.content,
          highlights: heroComponent.content.highlights || ['', '', '', ''],
          backgroundImage: heroBackgroundImageUrl,
          backgroundImageId: heroComponent.content.backgroundImageId || undefined,
          primaryButtonText: heroComponent.content.primaryButtonText || 'Download Brochure',
          secondaryButtonText: heroComponent.content.secondaryButtonText || 'Speak to Property Expert',
          showDownloadForm: heroComponent.content.showDownloadForm !== undefined ? 
            heroComponent.content.showDownloadForm : true
        };
      });
    } else {
      // Only set default if current state is not already default
      setHeroContent(prev => {
        if (prev.title === '' && prev.subtitle === '' && !prev.backgroundImageId) {
          return prev; // Already default, no change needed
        }
        
        return DEFAULT_HERO_CONTENT;
      });
    }
  }, [heroComponent, heroBackgroundImageUrl, setHeroContent]);
  
  // Update the hero content background image when heroBackgroundImageUrl changes
  useEffect(() => {
    if (!heroBackgroundImageUrl || !heroComponent) return;
    
    console.log('Updating hero content with latest background image URL:', heroBackgroundImageUrl);
    setHeroContent(prev => {
      // Only update if the URL has changed
      if (prev.backgroundImage === heroBackgroundImageUrl) {
        return prev;
      }
      
      return {
        ...prev,
        backgroundImage: heroBackgroundImageUrl
      };
    });
  }, [heroBackgroundImageUrl, heroComponent, setHeroContent]);

  // Hero-specific handlers
  const addHighlight = () => {
    setHeroContent(prev => ({
      ...prev,
      highlights: [...prev.highlights, '']
    }));
  };

  const removeHighlight = (index: number) => {
    setHeroContent(prev => ({
      ...prev,
      highlights: prev.highlights.filter((_, i) => i !== index)
    }));
  };

  const updateHighlight = (index: number, value: string) => {
    setHeroContent(prev => {
      const newHighlights = [...prev.highlights];
      newHighlights[index] = value;
      return {
        ...prev,
        highlights: newHighlights
      };
    });
  };

  const toggleDownloadForm = () => {
    setHeroContent(prev => ({
      ...prev,
      showDownloadForm: !prev.showDownloadForm
    }));
  };
  
  const handleBackgroundImageChange = (fileId: FileId | null, imageUrl: string) => {
    console.log('HeroSection received image change:', { 
      fileId, 
      imageUrl,
      projectId: projectId?.toString(),
      componentId: heroComponent?._id?.toString()
    });
    
    if (!imageUrl) {
      console.log('No image URL provided, skipping update');
      return;
    }
    
    setHeroContent(prev => {
      // Check if anything has actually changed to prevent unnecessary updates
      if (prev.backgroundImage === imageUrl && prev.backgroundImageId === fileId) {
        return prev;
      }
      
      // Store the actual file ID - the URL is just for immediate preview
      // When the component loads again, it will fetch the proper URL from the server
      const updated = {
        ...prev,
        backgroundImage: imageUrl,
        backgroundImageId: fileId || undefined
      };
      
      console.log('Updated hero content with new background:', {
        backgroundImage: updated.backgroundImage,
        backgroundImageId: updated.backgroundImageId
      });
      
      return updated;
    });
  };

  // AI Generation handlers
  const handleGenerateTitle = async () => {
    const content = await generate({
      prompt: 'Generate a compelling hero title for this real estate project',
      fieldType: FieldType.HERO_TITLE
    });
    if (content) {
      setHeroContent(prev => ({ ...prev, title: content }));
    }
  };

  const handleGenerateSubtitle = async () => {
    const content = await generate({
      prompt: 'Generate an engaging hero subtitle for this real estate project',
      fieldType: FieldType.HERO_SUBTITLE
    });
    if (content) {
      setHeroContent(prev => ({ ...prev, subtitle: content }));
    }
  };

  const handleGenerateHighlight = async (index: number) => {
    const content = await generate({
      prompt: 'Generate a compelling highlight for this real estate project',
      fieldType: FieldType.HERO_HIGHLIGHT
    });
    if (content) {
      updateHighlight(index, content);
    }
  };

  const saveHeroComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    // Log detailed information before saving
    console.log('Saving hero component with data:', {
      projectId: projectId.toString(),
      languageCode: selectedLanguage,
      componentType: 'hero',
      componentId: heroComponent?._id?.toString(),
      backgroundImage: heroContent.backgroundImage ? 'present' : 'absent',
      backgroundImageId: heroContent.backgroundImageId,
      backgroundImageUrl: heroContent.backgroundImage,
      contentSize: JSON.stringify(heroContent).length
    });

    // Validate that we have the image data to save
    if (!heroContent.backgroundImage && heroComponent?.content?.backgroundImage) {
      console.log('Preserving existing backgroundImage from component');
      setHeroContent(prev => ({
        ...prev,
        backgroundImage: heroComponent.content.backgroundImage
      }));
    }

    if (!heroContent.backgroundImageId && heroComponent?.content?.backgroundImageId) {
      console.log('Preserving existing backgroundImageId from component');
      setHeroContent(prev => ({
        ...prev,
        backgroundImageId: heroComponent.content.backgroundImageId
      }));
    }

    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'hero',
        order: 100, // First component
        content: heroContent,
        isActive: true,
        componentId: heroComponent?._id
      });
      
      console.log('Hero component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'Hero section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving hero component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save hero section'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hero Section</CardTitle>
        <CardDescription>
          Configure the main hero banner that appears at the top of your landing page
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <label htmlFor="hero-title" className="text-sm font-medium flex-1">
                Hero Title
              </label>
              <AIButton
                onClick={handleGenerateTitle}
                isLoading={isGenerating}
                tooltip="Generate title with AI"
              />
            </div>
            <RTLTextarea
              id="hero-title"
              language={selectedLanguage}
              value={heroContent.title}
              onChange={(e) => setHeroContent({ ...heroContent, title: e.target.value })}
              placeholder="Enter hero title"
              rows={2}
            />
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <label htmlFor="hero-subtitle" className="text-sm font-medium flex-1">
                Hero Subtitle
              </label>
              <AIButton
                onClick={handleGenerateSubtitle}
                isLoading={isGenerating}
                tooltip="Generate subtitle with AI"
              />
            </div>
            <RTLTextarea
              id="hero-subtitle"
              language={selectedLanguage}
              value={heroContent.subtitle}
              onChange={(e) => setHeroContent({ ...heroContent, subtitle: e.target.value })}
              placeholder="Enter hero subtitle"
              rows={3}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Background Image</label>
          {projectId && (
            <BackgroundImageUploader 
              projectId={projectId as Id<"projects">}
              componentId={heroComponent?._id}
              onImageChange={handleBackgroundImageChange}
              existingImage={heroContent.backgroundImage}
              existingImageId={heroContent.backgroundImageId}
            />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="primary-button" className="text-sm font-medium">
              Primary Button Text
            </label>
            <RTLInput
              id="primary-button"
              language={selectedLanguage}
              value={heroContent.primaryButtonText}
              onChange={(e) => setHeroContent({ ...heroContent, primaryButtonText: e.target.value })}
              placeholder="Primary button text"
            />
          </div>
          
          <div className="space-y-2">
            <label htmlFor="secondary-button" className="text-sm font-medium">
              Secondary Button Text
            </label>
            <RTLInput
              id="secondary-button"
              language={selectedLanguage}
              value={heroContent.secondaryButtonText}
              onChange={(e) => setHeroContent({ ...heroContent, secondaryButtonText: e.target.value })}
              placeholder="Secondary button text"
            />
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="download-form"
            checked={heroContent.showDownloadForm}
            onCheckedChange={toggleDownloadForm}
          />
          <Label htmlFor="download-form">
            Show download form option
          </Label>
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <label className="text-sm font-medium">Highlights</label>
            <Button 
              type="button" 
              variant="outline" 
              size="sm"
              onClick={addHighlight}
            >
              Add Highlight
            </Button>
          </div>
          
          {heroContent.highlights.map((highlight, index) => (
            <div key={index} className="flex items-center space-x-2">
              <RTLInput
                language={selectedLanguage}
                value={highlight}
                onChange={(e) => updateHighlight(index, e.target.value)}
                placeholder={`Highlight ${index + 1}`}
                className="flex-1"
              />
              <AIButton
                onClick={() => handleGenerateHighlight(index)}
                isLoading={isGenerating}
                tooltip="Generate highlight with AI"
              />
              <Button 
                type="button" 
                variant="ghost" 
                size="sm" 
                onClick={() => removeHighlight(index)}
                className="text-red-500"
              >
                Remove
              </Button>
            </div>
          ))}
        </div>

        <Button 
          onClick={saveHeroComponent} 
          disabled={isLoading}
          className="w-full"
        >
          {isLoading ? 'Saving...' : 'Save Hero Section'}
        </Button>
      </CardContent>
    </Card>
  );
}; 